import { createContext, useContext, ReactNode, useCallback, useRef } from 'react'
import { LogtoProvider, LogtoConfig, useLogto } from '@logto/react'

// LogTo configuration
const logtoConfig: LogtoConfig = {
  endpoint: import.meta.env.VITE_LOGTO_ENDPOINT || '',
  appId: import.meta.env.VITE_LOGTO_APP_ID || '',
  scopes: [
    'openid', 
    'profile', 
    'email',
    'roles',
    'urn:logto:scope:roles',          // DO NOT REMOVE THIS SCOPE, important for roles
    'urn:logto:scope:organizations'   // DO NOT REMOVE THIS SCOPE, important for roles
  ]
}

// Authentication context type
interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  error?: Error
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  getAccessToken: (resource?: string) => Promise<string | undefined>
  getIdTokenClaims: () => Promise<any>
}

// Create authentication context
const AuthContext = createContext<AuthContextType | null>(null)

// Auth provider component that wraps LogtoProvider
interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  return (
    <LogtoProvider config={logtoConfig}>
      <AuthContextProvider>{children}</AuthContextProvider>
    </LogtoProvider>
  )
}

// Internal provider that uses LogTo hooks
const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const {
    isAuthenticated,
    isLoading,
    error,
    signIn: logtoSignIn,
    signOut: logtoSignOut,
    getAccessToken: logtoGetAccessToken,
    getIdTokenClaims,
  } = useLogto()

  // Memoize token requests to prevent multiple calls
  const tokenCache = useRef<Map<string, Promise<string | undefined>>>(new Map())

  // Wrapper functions for LogTo methods
  const signIn = async () => {
    const callbackUrl = import.meta.env.VITE_CALLBACK_URL
    await logtoSignIn(callbackUrl)
  }

  const signOut = async () => {
    const appUrl = import.meta.env.VITE_APP_URL
    
    try {
      // Clear token cache on sign out
      tokenCache.current.clear()
      
      // Clear any stored tokens in localStorage
      const logtoKeys = Object.keys(localStorage).filter(key => key.startsWith('logto'))
      logtoKeys.forEach(key => localStorage.removeItem(key))
      
      await logtoSignOut(appUrl)
    } catch (err) {
      console.warn('Sign out error (continuing anyway):', err)
      // Force clear local storage even if LogTo sign out fails
      const logtoKeys = Object.keys(localStorage).filter(key => key.startsWith('logto'))
      logtoKeys.forEach(key => localStorage.removeItem(key))
      
      // Force reload to clear any stuck state
      window.location.reload()
    }
  }

  const getAccessToken = useCallback(async (resource?: string) => {
    const cacheKey = resource || 'default'
    
    // Check if we already have a pending request for this resource
    if (tokenCache.current.has(cacheKey)) {
      return tokenCache.current.get(cacheKey)
    }

    // Create a new request and cache it
    const tokenPromise = resource ? logtoGetAccessToken(resource) : logtoGetAccessToken()
    tokenCache.current.set(cacheKey, tokenPromise)

    try {
      const token = await tokenPromise
      return token
    } catch (err) {
      // Remove failed request from cache
      tokenCache.current.delete(cacheKey)
      console.warn('Failed to get access token:', err)
      throw err
    } finally {
      // Clean up successful requests after a short delay
      setTimeout(() => {
        tokenCache.current.delete(cacheKey)
      }, 1000)
    }
  }, [logtoGetAccessToken])

  // Handle authentication errors and clear invalid tokens
  const cleanError = (() => {
    if (!error) return undefined
    
    // If we have persistent invalid_grant errors, clear tokens and force re-auth
    if (error.message?.includes('invalid_grant') || error.message?.includes('Invalid token')) {
      console.warn('Clearing invalid tokens due to authentication error:', error.message)
      
      // Clear all LogTo tokens from localStorage
      const logtoKeys = Object.keys(localStorage).filter(key => key.startsWith('logto'))
      logtoKeys.forEach(key => localStorage.removeItem(key))
      
      // Clear token cache
      tokenCache.current.clear()
      
      // Don't show the error to user, just clear the invalid state
      return undefined
    }
    
    return error
  })()

  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    error: cleanError,
    signIn,
    signOut,
    getAccessToken,
    getIdTokenClaims,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use authentication context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext 